// ============================================================================
// FSA NOTE MODAL — DISCLOSURE AMOUNT CELL EDITOR (dual storage)
// ============================================================================
// Amount columns in additional disclosures mirror main-note classes:
// they carry BOTH an `actual` (full-precision Rupees) and a `rounded`
// (per-engagement-factor) value. Auditor types the actual; auto-round
// fills the rounded; user can override the rounded within tolerance.
//
// Storage convention (single row in the grid):
//   row[col.code]              = actual (canonical, used by formulas)
//   row[col.code + '__rounded'] = user-approved rounded value
//
// Rendering: two stacked NumericInputs per cell. Top is the actual,
// bottom is the rounded — labelled with the rounding-factor unit so the
// auditor knows which is which. Computed cells (is_computed = true) lock
// the actual input but keep the rounded editable so the auditor can still
// hold a rounding decision over the auto-derived figure.
//
// Tolerance: when the user has overridden the rounded value previously
// and a fresh actual change recomputes auto-rounded, we KEEP the user's
// override if the drift is within tolerance, else snap to the new auto.

import React from 'react';
import { NumericInput } from '../../shared/NumericInput';
import {
  autoRound,
  applyRoundingTolerance,
  type RoundingFactor,
} from '../../shared/rounding';

export interface AmountCellEditorProps {
  actualValue: number;
  roundedValue: number;
  factor: RoundingFactor | null | undefined;
  tolerance: number;
  /** TRUE for cells whose `actual` is formula-derived. Actual input locks; rounded stays editable. */
  isComputed: boolean;
  readOnly: boolean;
  onChange: (actual: number, rounded: number) => void;
  /** Optional helper text shown below the rounded input (e.g. "Lakhs"). */
  factorHint?: string;
  /**
   * Note-Modal-level Actual / Rounded toggle. When `'actual'`, only the
   * actual input is rendered (auto-round still updates the rounded
   * companion behind the scenes within tolerance). When `'rounded'`,
   * only the rounded input is rendered (actual stays as-is). Defaults
   * to `'both'` for backwards compatibility — older call sites that
   * don't pass the prop keep the dual editor. The Note Modal pipes
   * the toggle through to every disclosure grid.
   */
  displayMode?: 'actual' | 'rounded' | 'both';
}

export const AmountCellEditor: React.FC<AmountCellEditorProps> = ({
  actualValue,
  roundedValue,
  factor,
  tolerance,
  isComputed,
  readOnly,
  onChange,
  factorHint,
  displayMode = 'both',
}) => {
  const handleActualChange = (n: number) => {
    // Auto-round companion: recompute auto, but preserve user override
    // when within tolerance.
    const auto = autoRound(n, factor);
    const next = applyRoundingTolerance(roundedValue, auto, tolerance);
    onChange(n, next);
  };

  const handleRoundedChange = (n: number) => {
    // When the row is effectively "no factor" (factor === 'ACTUALS' — set
    // by the parent when the row is rounding_exempt or integer), the
    // actual and rounded slots are semantically the same value. Typing
    // in the rounded cell must update BOTH — otherwise Pass 2 of the
    // grid's recompute sees actual=0 vs rounded=N drift, exceeds
    // tolerance, and resets rounded back to autoRound(actual)=0,
    // silently losing the auditor's input.
    if (factor === 'ACTUALS') {
      onChange(n, n);
      return;
    }
    // Normal mode: rounded is a manual override of the auto-rounded
    // value; actual stays canonical.
    onChange(actualValue, n);
  };

  // Path Y: compact, spreadsheet-like cell inputs. Single-mode views drop
  // the muted/italic styling so the visible input reads like a real grid
  // cell. The dual mode (legacy callers) keeps the older treatment so
  // they remain visually unambiguous.
  const showActual = displayMode !== 'rounded';
  const showRounded = displayMode !== 'actual';

  // Out-of-tolerance mark — same signal the main-note cells give: the saved
  // rounded no longer matches the actual under the CURRENT factor (typically
  // a value rounded before the factor changed). Nothing is changed for the
  // auditor; Reset rounding re-rounds the note. Computed cells are excluded:
  // their rounded is the formula over rounded operands, not actual ÷ factor.
  const autoNow = autoRound(actualValue, factor);
  const outOfTolerance = !isComputed && Math.abs(roundedValue - autoNow) > tolerance;

  return (
    <div style={displayMode === 'both' ? wrapStyle : wrapSingleStyle}>
      {showActual && (
        <NumericInput
          value={actualValue}
          onChange={handleActualChange}
          readOnly={readOnly || isComputed}
          disabled={readOnly || isComputed}
          className="form-input"
          style={displayMode === 'both' ? actualInputStyle : compactCellInputStyle}
          title={isComputed ? 'Computed actual (formula-derived)' : 'Actual (full-precision Rupees)'}
          placeholder=""
        />
      )}
      {showRounded && (
        <NumericInput
          value={roundedValue}
          onChange={handleRoundedChange}
          readOnly={readOnly}
          disabled={readOnly}
          className="form-input"
          style={{
            ...(displayMode === 'both' ? roundedInputStyle : compactCellInputStyle),
            ...(outOfTolerance ? outOfToleranceStyle : {}),
          }}
          title={outOfTolerance
            ? `Rounded ${formatAmount(roundedValue)} differs from ${formatAmount(autoNow)} (actual under the current factor). Use Reset rounding to re-round.`
            : `Rounded${factorHint ? ` (${factorHint})` : ''}`}
          placeholder=""
        />
      )}
    </div>
  );
};

// ───── display-only variant (computed actual readout) ───────────────────────

interface AmountCellReadoutProps {
  actualValue: number;
  roundedValue: number;
}

export const AmountCellReadout: React.FC<AmountCellReadoutProps> = ({ actualValue, roundedValue }) => (
  <div style={wrapStyle}>
    <span style={actualReadoutStyle}>
      {formatAmount(actualValue)}
    </span>
    <span style={roundedReadoutStyle}>
      {formatAmount(roundedValue)}
    </span>
  </div>
);

function formatAmount(v: number): string {
  return v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ───── styles ───────────────────────────────────────────────────────────────

const wrapStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};
// Single-mode wrapper — no internal gap, the cell IS the input. Used
// when the parent grid passes displayMode === 'actual' | 'rounded'.
const wrapSingleStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
};
// Path Y compact-cell input — spreadsheet-like cell input. A faint
// border (form-input variable) ensures empty cells stay visually
// discoverable when the column shows 0 / blank — without it, empty
// amount rows render as invisible cells (no border, no value, no
// background). Keeps the compact feel while ensuring auditors can see
// where to click to edit.
const compactCellInputStyle: React.CSSProperties = {
  width: '100%',
  fontSize: '0.78rem',
  padding: '2px 4px',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
  border: '1px solid var(--form-border-color)',
  borderRadius: 3,
  background: 'transparent',
  outline: 'none',
};
const actualInputStyle: React.CSSProperties = {
  width: '100%',
  fontSize: '0.78rem',
  padding: '2px 6px',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};
// Rounded sub-input renders slightly smaller and visually muted so the
// auditor reads "actual is the canonical entry, rounded is its print companion".
const roundedInputStyle: React.CSSProperties = {
  width: '100%',
  fontSize: '0.72rem',
  padding: '2px 6px',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
  background: 'var(--bg-tertiary, #f1f5f9)',
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
};
// Amber — same colour as the main-note drift dot / out-of-tolerance cells.
const outOfToleranceStyle: React.CSSProperties = {
  borderColor: '#d97706',
  boxShadow: 'inset 0 0 0 1px #d97706',
};
const actualReadoutStyle: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
  fontSize: '0.78rem',
  textAlign: 'right',
};
const roundedReadoutStyle: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
  fontSize: '0.72rem',
  textAlign: 'right',
};

export default AmountCellEditor;
